import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, List, Tag, Tooltip, Typography } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { Dynamics } from '@/services/dynamics/dynamics';
import { useStore } from '@/stores';
import {
  ICrmMarketingList,
  MemberType,
} from '@/shared/interfaces/crm-marketing-list.interface';
import InputSearch from '@/components/InputSearch/InputSearch';
import { formDate } from '@/services/utils/formatDate';
import { CALL_PAGE_TYPE } from '@/shared/constants/callPage';
import './MarketingLists.scss';

type TagMapType = { [K in MemberType]: string };

const { Title } = Typography;

const tagMap: TagMapType = {
  account: '#87d068',
  contact: '#108ee9',
  lead: '#2db7f5',
};

export const MarketingLists = () => {
  const {
    callStore: { callPage },
  } = useStore();

  const { page } = callPage;
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<ICrmMarketingList[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue?.(e.currentTarget.value);
    },
    [],
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!searchValue) {
      getMarketingList();

      return;
    }

    const timerID = setTimeout(async () => {
      getMarketingList();
    }, 1000);

    return () => {
      clearTimeout(timerID);
    };
  }, [searchValue]);

  const getMarketingList = async () => {
    setLoading(true);
    const clearValue = searchValue.trim();
    const filter = `contains(listname, '${clearValue}')`;
    const data = await Dynamics.getMarketingList(filter);
    setList(data);
    setLoading(false);
  };

  const onRefresh = () => {
    if (searchValue) {
      setSearchValue('');
    } else {
      getMarketingList();
    }
  };

  const moveToCertainList = (list: ICrmMarketingList) => {
    navigate(`/dialer/${list.listId}`, { state: { marketingList: list } });
  };

  return (
    <div
      className={`marketing-lists ${page === CALL_PAGE_TYPE.MINIMIZED ? 'shortened' : ''}`}
    >
      <div className="marketing-lists__header">
        <Title level={5}>Marketing Lists</Title>
        <Tooltip title="Reset/Refresh">
          <Button
            data-testid="refresh-marketing-lists"
            onClick={onRefresh}
            type="dashed"
            shape="circle"
            icon={<RedoOutlined />}
          />
        </Tooltip>
      </div>
      <InputSearch
        value={searchValue}
        data-testid="marketing-lists-search-input"
        onChange={handleSearchChange}
        placeholder="Find by name"
      />
      <List
        className="marketing-lists__content"
        loading={loading}
        dataSource={list}
        renderItem={(item) => (
          <List.Item
            className="marketing-lists__item"
            onClick={() => moveToCertainList(item)}
            key={item.listId}
          >
            <List.Item.Meta
              title={item.listName}
              description={formDate(item.createdOn)}
            />
            <div className="marketing-lists__type">
              <Badge
                status={item.type === 'Static' ? 'success' : 'processing'}
                text={item.type}
              />
              <Badge>
                <Tag color={tagMap[item.memberType]}>
                  {item.memberType}
                  {item.memberCount ? (
                    <span className="ribbon-count">{item.memberCount}</span>
                  ) : (
                    <></>
                  )}
                </Tag>
              </Badge>
            </div>
          </List.Item>
        )}
      />
    </div>
  );
};
